
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractFormDialog, AbstractFormDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';

import { select, Store } from '@ngrx/store';
import { Subscription, of, combineLatest } from 'rxjs';
import { startWith, filter, map, switchMap, shareReplay, take } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';
import { selectSalesOrderPeriodsWithMaxPeriodById, isPeriodExpired } from 'src/app/store/selectors';

import { Package, OrderLine, CropAid, OrderLineDialogResult, SourceEnum } from '../../models';

export interface OrderLineDialogData extends AbstractFormDialogData {
  orderLine: OrderLine;
  packages: Package[];
  cropAids: CropAid[];
  cropAidId?: number;
}

@Component({
  selector: 'app-orderline-dialog',
  templateUrl: './order-line-dialog.component.html',
  standalone: false,
})
export class OrderLineDialogComponent extends AbstractFormDialog<OrderLineDialogResult> implements OnInit, OnDestroy {
  data = inject<OrderLineDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<OrderLineDialogComponent, AbstractDialogResultData<OrderLineDialogResult>>>(MatDialogRef);
  private readonly store = inject<Store<SiteState>>(Store);

  formGroup = this.formBuilder.group({
    periodId: [this.data.orderLine.periodId, [Validators.required]],
    packageId: [this.data.orderLine.packageId, [Validators.required]],
    count: [this.data.orderLine.count, [Validators.required, Validators.min(1)]],
    id: this.data.orderLine.id,
    status: this.data.orderLine.status,
    deliveryPeriodMoment: this.data.orderLine.deliveryPeriodMoment,
  });
  hasInitialProduct = !!this.data.cropAidId;
  cropAidId = new FormControl({ value: this.data.cropAidId, disabled: this.hasInitialProduct }, [Validators.required]);
  periods$ = this.store.pipe(select(selectSalesOrderPeriodsWithMaxPeriodById(this.data.orderLine.periodId)));
  filteredCropAids$ = this.cropAidId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.filter(value, [...this.data.cropAids])),
  );
  selectedCropAid$ = this.cropAidId.valueChanges.pipe(
    startWith(this.data.cropAidId),
    map((cropAidId: number) => (cropAidId ? this.getCropAid(cropAidId) : null)),
  );
  isOneSapCropAid: boolean;

  isPeriodExpired$ = this.formGroup.get('periodId').valueChanges.pipe(
    startWith(this.data.orderLine.periodId),
    switchMap((periodId) => (periodId ? this.store.pipe(select(isPeriodExpired(periodId))) : of(false))),
    shareReplay(1),
  );

  selectedPackage$ = this.formGroup.get('packageId').valueChanges.pipe(
    startWith(this.data.orderLine.packageId),
    map((packageId: number) => (packageId ? this.getPackage(packageId) : null)),
  );

  areCropAidAndPackageActive$ = this.formGroup.get('packageId').valueChanges.pipe(
    startWith(this.data.orderLine.packageId),
    map((packageId: number) => {
      const selectedCropAid = this.data.cropAids.find((p) => p.id === this.cropAidId.value);
      const selectedPackage = this.data.packages.find((p) => p.id === packageId);

      return !selectedCropAid || (selectedCropAid.isActive && (selectedPackage == null || selectedPackage.isActive));
    }),
  );

  availablePackages: Package[] = [];

  private subscription = new Subscription();

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.isPeriodExpired$, this.periods$]).subscribe(([periodExpired, periods]) => {
        setTimeout(() => {
          if (!periodExpired && (!periods || !periods.length)) {
            this.formGroup.get('periodId').setErrors({ incorrect: true });
            this.formGroup.get('periodId').markAsTouched();
          }
        }, 100);
      }),
    );

    this.subscription.add(
      this.selectedCropAid$.subscribe((cropAid: CropAid) => {
        if (!cropAid) {
          this.cropAidId.setValue(null, { emitModelToViewChange: false, emitEvent: false });
          this.formGroup.get('packageId').reset();
        }

        if (this.cropAidId.value !== this.data.cropAidId) {
          this.formGroup.get('periodId').reset();
          this.formGroup.get('packageId').reset();
          this.formGroup.get('count').reset();
        }

        this.availablePackages = cropAid ? this.getAvailablePackages(cropAid.id) : [];
        this.isOneSapCropAid = cropAid?.source === SourceEnum.OneSAP;
      }),
    );
  }

  displayCropAid() {
    return (cropAidId?: number) => (cropAidId ? this.data.cropAids.find((cropAid) => cropAid.id === cropAidId)?.name : '');
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getCropAid(cropAidId: number) {
    return this.data.cropAids.find(({ id }) => id === cropAidId);
  }

  areCropAidAndPackageActive() {
    const selectedCropAid = this.data.cropAids.find((p) => p.id === this.cropAidId.value);
    const selectedPackage = this.data.packages.find((p) => p.id === this.formGroup.get('packageId').value);

    return !selectedCropAid || (selectedCropAid.isActive && (selectedPackage == null || selectedPackage.isActive));
  }

  confirm($event: OrderLineDialogResult) {
    this.isPeriodExpired$.pipe(take(1)).subscribe((periodExpired) => {
      if (this.areCropAidAndPackageActive() && !periodExpired) {
        super.confirm({ ...$event, cropAidId: this.cropAidId.value as number });
      }
    });
  }

  getPackage(packageId: number) {
    return this.data.packages.find(({ id }) => packageId === id);
  }

  getAvailablePackages(cropAidId?: number) {
    if (!cropAidId) {
      return [];
    }
    const productId = this.getCropAid(cropAidId)?.productId;
    if (!productId) {
      return [];
    }

    return this.data.packages.filter((pack) => pack.productId === productId && pack.isActive);
  }

  private filter(value: string, items: CropAid[]) {
    return items
      .sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
        return 0;
      })
      .filter((option) => option.name.toLowerCase().includes(value.toLowerCase()));
  }
}
